'use client';

import { useEffect, useState } from 'react';
import config from 'config';
import { inputDateFormat, ROUTES } from 'constant';
import { useRouter } from 'next/navigation';
import { todayDate } from 'services';
import { useGetAppointmentsQuery } from 'services/bookingApi';

import {
  Box,
  Flex,
  Stack,
  Text,
  useBreakpointValue,
  useTheme
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  setAppointment,
  setIsAuthRedirection,
  setServiceActiveStep,
  setStep
} from 'store/reducers/bookingAppointmentSlice';
import { setRedirectFlow } from 'store/reducers/userSlice';

import {
  buildLoginUrlWithReturnPath,
  formatPhoneNumbers,
  formatPhoneToFormat,
  rememberCurrentPageForPostAuthLogin
} from 'utils';

import {
  AppointmentInterface,
  BookingSteps,
  BookServiceSteps,
  LocationFlowType,
  PickTimeTabType
} from 'types';

import { Button, Divider, Link, MultiStepHeader } from 'ui';
import {
  AdvancedSearch,
  AppointmentData,
  AppointmentDetailsList,
  BoxTabs,
  CalendarSearch
} from './components';
import {
  DateRange,
  FormDataWithDateRange
} from './components/AdvancedSearch/helpers';

const {
  booking: { time, defaultProvider },
  contacts: { phone }
} = config;

interface TimeStepProps {
  locationId: number;
  onBack: () => void;
  onNext: () => void;
  onScrollToTop: () => void;
}

const TimeStep = ({
  locationId,
  onNext,
  onBack,
  onScrollToTop
}: TimeStepProps) => {
  const {
    colors: {
      palette: {
        booking: { stepTime }
      }
    }
  } = useTheme();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { user, selectedLocation } = useAppSelector((state) => state.user);
  const { selectedServices, selectedProviders, selectedAddons, moveApptId } =
    useAppSelector((state) => state.bookingAppointment);

  const [isResultsTab, setIsResultsTab] = useState(false);
  const [selectedDate, setSelectedDate] = useState<DateRange | null>(null);
  const [selectedDateRange, setSelectedDateRange] =
    useState<FormDataWithDateRange | null>(null);
  const [tabIdx, setTabIdx] = useState<PickTimeTabType>(
    PickTimeTabType.CALENDAR_SEARCH
  );

  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'base' }
  );

  const isMoveAppointment = !!moveApptId;
  const showResultContent = isResultsTab && isMobile;
  const locationPhone = selectedLocation?.phone || phone;
  const formattedPhone = formatPhoneNumbers(locationPhone);
  const selectedAddonsArray = Object.values(selectedAddons).flat(2);
  const servicesToVisit = [...selectedAddonsArray, ...selectedServices].map(
    (service) => ({
      id: service.id,
      attendees: user?.id || null,
      provider: selectedProviders[service.id]?.id || defaultProvider.id
    })
  );

  const {
    isLoading,
    isFetching,
    currentData: appointments
  } = useGetAppointmentsQuery(
    {
      locid: locationId,
      service: servicesToVisit,
      date:
        tabIdx === PickTimeTabType.ADVANCED_SEARCH
          ? selectedDateRange
          : selectedDate,
      isAdvancedSearch: tabIdx === PickTimeTabType.ADVANCED_SEARCH
    },
    {
      skip: !(selectedDate?.startDate || selectedDateRange?.startDate),
      refetchOnMountOrArgChange: true
    }
  );

  const handleBookAnotherService = () => {
    dispatch(setStep(BookingSteps.SERVICE));
    dispatch(setServiceActiveStep(BookServiceSteps.Services));
  };

  const handleNext = async (appointment: AppointmentInterface) => {
    if (user) {
      dispatch(setAppointment(appointment));
      dispatch(setRedirectFlow(null));
      onNext();
    } else {
      dispatch(setRedirectFlow(LocationFlowType.BOOK));
      dispatch(setAppointment(appointment));
      dispatch(setIsAuthRedirection(true));
      const returnPath = `${window.location.pathname}${window.location.search}`;
      rememberCurrentPageForPostAuthLogin();
      router.push(buildLoginUrlWithReturnPath(ROUTES.login, returnPath));
    }
  };

  const handleShowSearchTab = () => {
    setIsResultsTab(true);
  };

  const handleHideSearchTab = () => {
    setIsResultsTab(false);
  };

  const onTabChange = (index: PickTimeTabType) => {
    handleHideSearchTab();

    setTabIdx(index);
  };

  const showFirstAvailableAppointments = () => {
    const endDate = todayDate.add(7, 'day');
    const startDateFormatted = todayDate.format(inputDateFormat);
    const endDateFormatted = endDate.format(inputDateFormat);

    setSelectedDate({
      startDate: startDateFormatted,
      endDate: endDateFormatted
    });

    handleShowSearchTab();
  };

  const handleSelectDate = (date: string) => {
    setSelectedDate({
      startDate: date,
      endDate: date
    });

    handleShowSearchTab();
  };

  const handleAdvancedSearch = (params: FormDataWithDateRange) => {
    setSelectedDateRange(params);

    handleShowSearchTab();
  };

  const tabsData = [
    {
      label: 'Calendar search',
      content: (
        <AppointmentData
          showResultContent={!!showResultContent}
          appointments={appointments}
          onSelectAppointment={handleNext}
          selectedDate={selectedDate}
          isLoading={isLoading || isFetching}
        >
          <CalendarSearch
            onChange={handleSelectDate}
            onShowFirstAvailable={showFirstAvailableAppointments}
            btnText='First Available'
            withButton
          />
        </AppointmentData>
      )
    },
    {
      label: 'Advanced search',
      content: (
        <AppointmentData
          showResultContent={!!showResultContent}
          appointments={appointments}
          onSelectAppointment={handleNext}
          selectedDate={selectedDate}
          isLoading={isLoading || isFetching}
        >
          <AdvancedSearch onSearch={handleAdvancedSearch} />
        </AppointmentData>
      )
    },
    {
      label: 'Appointment details',
      content: (
        <AppointmentDetailsList
          onMoveToServiceStep={handleBookAnotherService}
          isMoveAppt={isMoveAppointment}
        />
      )
    }
  ];

  useEffect(() => {
    onScrollToTop();
  }, [onScrollToTop]);

  return (
    <Box w='100%'>
      <Stack
        w='100%'
        justifyContent='center'
        alignItems='center'
        mx='auto'
        gap={0}
      >
        <MultiStepHeader
          title={showResultContent ? 'Search Results' : time.title}
          subTitle={showResultContent ? 'See Results Below' : time.description}
          headingColor={stepTime.headingColor}
          descriptionColor={stepTime.descriptionColor}
          mt={{ base: '30px', md: '40px', lg: '50px' }}
          mb={{ base: '5px', md: '31px' }}
          descriptionPX={time.paddings}
        />

        <Stack
          maxW='916px'
          mx='auto'
          gap={{ base: '15px', xs: '20px', md: '31px' }}
        >
          <Box
            display={{
              base: showResultContent ? 'block' : 'none',
              md: 'none'
            }}
          >
            <Button
              variant='text'
              color={stepTime.searchAgainButtonColor}
              textDecoration='underline'
              onClick={handleHideSearchTab}
              fontSize='14px'
              fontWeight='bold'
              mb='10px'
              py='10px'
            >
              Search again
            </Button>
          </Box>

          <Text
            textAlign='center'
            color={stepTime.subtitleColor}
            fontSize='14px'
            mb='23px'
            mt={{ base: '10px', md: '0' }}
          >
            {time.subtitle}
            <Link
              to={`tel:${formattedPhone}`}
              sx={{
                fontSize: '14px',
                color: stepTime.phoneLinkColor,
                fontFamily: 'main',
                fontWeight: 'bold',
                _hover: { textDecoration: 'underline' }
              }}
            >
              {formatPhoneToFormat(formattedPhone)}
            </Link>
          </Text>
        </Stack>

        <Divider
          display={{
            base: showResultContent ? 'none' : 'block',
            md: 'block'
          }}
          h='1px'
          bg={stepTime.dividerColor}
          mb='20px'
          border='none'
        />

        <BoxTabs
          tabs={tabsData}
          onTabChange={onTabChange}
          isHiddenTabs={!!showResultContent}
        />
      </Stack>

      <Flex
        direction={{ base: 'column-reverse', md: 'row' }}
        mt={{ base: '40px', md: '50px' }}
        justifyContent='center'
        alignItems='center'
        gap='20px'
        w='100%'
      >
        <Button variant='outline' w='150px' onClick={onBack}>
          Back
        </Button>
      </Flex>
    </Box>
  );
};

export default TimeStep;
